import type { FC } from 'react';
import { Menu, MenuItem } from '@mui/material';

export type MATGridContextMenuPosition = {
  mouseX: number | null;
  mouseY: number | null;
};

type MATGridContextMenuProps = {
  open: boolean;
  position: MATGridContextMenuPosition;
  columnField?: string;
  cellRaw?: any;
  onClose: () => void;
  gridApi?: any;
  filterableColumns?: ReadonlySet<string>;
  onFilter?: (columnField: string, value: string) => void;
};

const MATGridContextMenu: FC<MATGridContextMenuProps> = ({
  open,
  position,
  columnField,
  cellRaw,
  onClose,
  gridApi,
  filterableColumns,
  onFilter,
}) => {
  const copyText = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (_) {
      // Fallback for older browsers
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
  };

  const formatClipboard = (val: any): string => {
    if (val == null) return '';
    if (typeof val === 'object') {
      try {
        return JSON.stringify(val);
      } catch {
        return String(val);
      }
    }
    return String(val);
  };

  const handleCopy = () => {
    if (cellRaw != null) {
      copyText(formatClipboard(cellRaw));
      onClose();
      return;
    }
    // Fallback to focused cell value if available
    const focused = gridApi?.getFocusedCell?.();
    if (focused) {
      const rowNode = gridApi?.getDisplayedRowAtIndex?.(focused.rowIndex);
      const val = rowNode?.data?.[focused.column?.getColId?.() ?? ''];
      if (val != null) copyText(formatClipboard(val));
    }
    onClose();
  };

  const handleFilter = () => {
    if (!columnField || !onFilter) return;
    const filterValue = formatClipboard(cellRaw);
    if (filterValue) {
      onFilter(columnField, filterValue);
    }
    onClose();
  };

  // Check if the current column is filterable
  const isFilterable = columnField && filterableColumns?.has(columnField);

  return (
    <Menu
      open={open}
      onClose={onClose}
      anchorReference="anchorPosition"
      anchorPosition={
        position.mouseY !== null && position.mouseX !== null
          ? { top: position.mouseY, left: position.mouseX }
          : undefined
      }
      PaperProps={{
        sx: {
          minWidth: 120,
          '& .MuiMenuItem-root': {
            fontSize: 12,
            lineHeight: 1.2,
            minHeight: 28,
            py: 0.5,
            px: 1,
          },
          '& .MuiList-root': {
            py: 0.25,
          },
        },
        elevation: 3,
      }}
    >
      <MenuItem onClick={handleCopy}>Copy</MenuItem>
      {isFilterable && cellRaw != null && cellRaw !== '' && (
        <MenuItem onClick={handleFilter}>Filter</MenuItem>
      )}
    </Menu>
  );
};

export default MATGridContextMenu;
